import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import ForwardIcon from '@mui/icons-material/Forward';
import { Order } from 'models/order';
import { Steps } from 'models/workflow-step';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { subcontractTriggerPendingTemplate } from 'apollo/templates/workFlow';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { permissions } from 'utils/permission';

/** DD-3488: the subcontract wizard for an order, started from a workflow step. */
export const subcontractFlowPath = (orderId: string) => `/orders/subcontract/${orderId}`;

/** True when a single-order move into `step` should open the subcontract wizard. */
export const opensSubcontractFlow = (step: Steps | null | undefined) =>
    Boolean(step?.initiatesSubcontract) && !permissions.isExternalTechnician(currentUserVar().role);

type SubcontractTriggerButtonProps = { order: Order; fullWidth?: boolean };

/**
 * DD-3488: while the order sits in a step that initiates subcontracting and that visit has
 * produced no live sub-order (the wizard was abandoned, or a bulk or automatic move put it
 * there), the lab can start the wizard again. The backend decides whether it is pending.
 */
const SubcontractTriggerButton: FC<SubcontractTriggerButtonProps> = ({ order, fullWidth }) => {
    const { t: translate } = useTranslation(['workflow']);
    const navigate = useNavigate();
    const { showBoundary } = useDenthubErrorBoundary();
    const currentStep = currentOrgVar().workFlow?.steps.find(
        (step) => step._id === order.workFlow?.currentStep?.stepId,
    );
    const { data } = useQuery(subcontractTriggerPendingTemplate, {
        variables: { orderId: order._id },
        skip: !opensSubcontractFlow(currentStep),
        fetchPolicy: 'cache-and-network',
        onError: showBoundary,
    });

    if (!data?.subcontractTriggerPending) {
        return null;
    }
    return (
        <Box mb={fullWidth ? 1 : 0}>
            <Button
                fullWidth={fullWidth}
                size={fullWidth ? 'medium' : 'small'}
                variant="contained"
                startIcon={<ForwardIcon />}
                onClick={() => navigate(subcontractFlowPath(order._id))}
                data-test="work-flow-actions.create-sub-order-btn"
            >
                {translate('workflow:CREATE_SUB_ORDER_FOR_STEP')}
            </Button>
        </Box>
    );
};

export default SubcontractTriggerButton;
